import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Gamepad2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", form);
      login(res.data.token, res.data.user);
      toast.success(`Bem-vindo(a), ${res.data.user.name}!`);
      navigate(res.data.user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao entrar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg p-6 relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-brand-purple/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-brand-blue/10 rounded-full blur-3xl" />
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 p-8 sm:p-10">
        <Logo />
        <h1 className="mt-8 font-heading text-3xl font-black text-brand-ink">Entrar</h1>
        <p className="mt-2 text-brand-muted">Acesse seu painel de professor.</p>

        <Button
          onClick={googleLogin}
          variant="outline"
          className="mt-6 w-full rounded-xl h-12 border-2 gap-2"
          data-testid="google-login-btn"
        >
          <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" />
          Continuar com Google
        </Button>

        <div className="my-6 flex items-center gap-3 text-xs text-brand-muted">
          <span className="flex-1 h-px bg-slate-200" /> ou <span className="flex-1 h-px bg-slate-200" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-xl h-11" data-testid="input-login-email" />
          </div>
          <div>
            <Label htmlFor="password">Senha</Label>
            <Input id="password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 rounded-xl h-11" data-testid="input-login-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full h-12 bg-brand-blue hover:bg-blue-700 font-semibold" data-testid="submit-login">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Entrar"}
          </Button>
        </form>

        <Link to="/esqueci-senha" className="mt-4 block text-center text-sm text-brand-blue hover:underline" data-testid="forgot-link">
          Esqueci minha senha
        </Link>

        <p className="mt-6 text-center text-sm text-brand-muted">
          Não tem conta?{" "}
          <Link to="/cadastro" className="text-brand-blue font-semibold hover:underline">
            Criar grátis
          </Link>
        </p>
        <Link to="/jogar" className="mt-4 flex items-center justify-center gap-2 text-sm text-brand-purple font-semibold hover:underline">
          <Gamepad2 className="w-4 h-4" /> Sou aluno, quero entrar em um jogo
        </Link>
      </div>
    </div>
  );
}
